import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api, { API_HOST } from '../../utils/api';
import { HiOutlineDownload, HiOutlineTrash, HiOutlineSparkles, HiOutlineDocumentText } from 'react-icons/hi';

const SALES_ASSET_LABELS = {
  sales_proposal: 'Proposal',
  sales_deck: 'Sales Deck',
};

export default function SalesDashboard() {
  const [profile, setProfile] = useState(null);
  const [recentAssets, setRecentAssets] = useState([]);
  const [allAssets, setAllAssets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [p, a] = await Promise.all([
          api.get('/sales/profile'),
          api.get('/sales/assets')
        ]);
        setProfile(p.data);
        setAllAssets(a.data);
        setRecentAssets(a.data.slice(0, 5));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const getClientContext = (asset) => {
    try {
      const opts = asset.extraOptions ? JSON.parse(asset.extraOptions) : {};
      return opts.clientContext || {};
    } catch { return {}; }
  };

  const completedCount = allAssets.filter(a => a.status === 'completed').length;
  const proposalCount = allAssets.filter(a => a.assetType === 'sales_proposal' && a.status === 'completed').length;
  const deckCount = allAssets.filter(a => a.assetType === 'sales_deck' && a.status === 'completed').length;

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-v2-blue"></div></div>;

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-1">
        Welcome back{profile?.displayName ? `, ${profile.displayName}` : ''}
      </h1>
      <p className="text-gray-500 mb-8">Sales dashboard - generate personalized proposals and decks for your prospects.</p>

      {/* Stats + Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Link to="/sales/generate" className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-md transition-shadow group">
          <HiOutlineSparkles className="w-8 h-8 text-v2-blue mb-2 group-hover:scale-110 transition-transform" />
          <div className="font-medium text-gray-900">Generate Asset</div>
          <div className="text-sm text-gray-500">Create a new sales asset</div>
        </Link>
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <HiOutlineDocumentText className="w-8 h-8 text-green-600 mb-2" />
          <div className="text-2xl font-bold text-gray-900">{completedCount}</div>
          <div className="text-sm text-gray-500">Total Assets</div>
        </div>
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="text-2xl font-bold text-gray-900">{proposalCount}</div>
          <div className="text-sm text-gray-500">Proposals</div>
        </div>
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="text-2xl font-bold text-gray-900">{deckCount}</div>
          <div className="text-sm text-gray-500">Sales Decks</div>
        </div>
      </div>

      {/* Recent assets */}
      <h2 className="text-lg font-semibold text-gray-800 mb-4">Recent Generations</h2>
      {recentAssets.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
          No assets generated yet. Start by generating your first asset!
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Type</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Company</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Topic</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Date</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {recentAssets.map(asset => {
                const ctx = getClientContext(asset);
                return (
                  <tr key={asset.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">{SALES_ASSET_LABELS[asset.assetType] || asset.assetType}</td>
                    <td className="px-4 py-3 text-sm font-medium">{ctx.companyName || <span className="text-gray-400">-</span>}</td>
                    <td className="px-4 py-3 text-sm">{asset.topic?.title}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{new Date(asset.createdAt).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 text-xs rounded-full font-medium ${
                        asset.status === 'completed' ? 'bg-green-100 text-green-800' :
                        asset.status === 'failed' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>{asset.status}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {asset.status === 'completed' && asset.filePath && (
                          <a href={`${API_HOST}/api/sales/assets/${asset.id}/download`} className="text-v2-blue hover:underline text-sm flex items-center gap-1">
                            <HiOutlineDownload className="w-4 h-4" /> Download
                          </a>
                        )}
                        {asset.status === 'failed' && (
                          <button onClick={async () => {
                            if (!window.confirm('Remove this failed generation?')) return;
                            try { await api.delete(`/sales/assets/${asset.id}`); setRecentAssets(prev => prev.filter(a => a.id !== asset.id)); } catch {}
                          }} className="text-gray-400 hover:text-red-600 text-sm flex items-center gap-1" title="Remove">
                            <HiOutlineTrash className="w-4 h-4" /> Remove
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
